import { z } from "zod";

// Validação centralizada para dados de contato obrigatórios em capturas de lead.
// Aplicada tanto no cliente (UX) quanto no servidor (garantia de integridade).

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const contactSchema = z.object({
  nome: z
    .string()
    .trim()
    .min(2, "Informe seu nome.")
    .max(120, "Nome muito longo."),
  email: z
    .string()
    .trim()
    .toLowerCase()
    .max(200, "E-mail muito longo.")
    .refine((v) => EMAIL_RE.test(v), { message: "Informe um e-mail válido." }),
  telefone: z
    .string()
    .trim()
    .max(40, "Telefone inválido.")
    .refine(
      (v) => {
        const digits = v.replace(/\D+/g, "");
        return digits.length >= 10 && digits.length <= 13;
      },
      { message: "Informe um WhatsApp válido com DDD." },
    ),
});

export type ContactInput = z.infer<typeof contactSchema>;

/** Valida + normaliza (trim/lowercase email). Lança Error em caso de falha. */
export function ensureValidContact(input: {
  nome?: string;
  email?: string;
  telefone?: string;
}): ContactInput {
  const parsed = contactSchema.safeParse({
    nome: input.nome ?? "",
    email: input.email ?? "",
    telefone: input.telefone ?? "",
  });
  if (!parsed.success) {
    const first = parsed.error.issues[0]?.message ?? "Dados de contato inválidos.";
    throw new Error(first);
  }
  return parsed.data;
}

/** Versão client-side: retorna mensagem de erro ou null. */
export function validateContactClient(input: {
  nome: string;
  email: string;
  telefone: string;
}): string | null {
  const parsed = contactSchema.safeParse(input);
  if (parsed.success) return null;
  return parsed.error.issues[0]?.message ?? "Dados inválidos.";
}

// Helpers Zod para reuso direto em schemas de server functions.
export const nomeField = contactSchema.shape.nome;
export const emailField = contactSchema.shape.email;
export const telefoneField = contactSchema.shape.telefone;

/**
 * URLs/UTMs/fbc/fbp em campanhas pagas (Meta Ads, GCLID, tracker_trk) facilmente
 * passam de 800-1500 chars. Truncamos no servidor em vez de rejeitar o lead —
 * limites rígidos custaram leads reais. Use trackingString() em todos os schemas
 * de captura.
 */
export const trackingString = (max: number) =>
  z.preprocess((v) => {
    if (typeof v !== "string") return v;
    return v.slice(0, max);
  }, z.string().max(max));


